import {ChangeDetectorRef, Component, forwardRef, Input, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormArray, FormGroup, NG_VALUE_ACCESSOR, NgForm, UntypedFormBuilder, Validators} from '@angular/forms';
import {FinanceService} from '@service/services/server/finance.service';
import {ApplicantModel, ApplicationModel, BeneficialOwnerModel, KeyIdentifierModel} from '@service/models';
import {ApplicantType} from '@service/enum/process-state.enum';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
@Component({
    selector: 'creditsnap-additional-owners',
    templateUrl: './additional-owners.component.html',
    styleUrls: ['./additional-owners.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AdditionalOwnersComponent),
            multi: true,
        },
    ],
})
export class AdditionalOwnersComponent implements ControlValueAccessor {
    @ViewChild('companyOtherOwnersForm') companyOtherOwnersForm: NgForm;
    @Input('data-test-id') testId: string = 'additional-owners'; 
    ownershipForm: FormGroup;
    keyIdentifier: KeyIdentifierModel;

    certifiedOwnersBox = false;
    showErrorForCertifiedBox = false;
    application: ApplicationModel;
    otherOwners: ApplicantModel[];
    primaryApplicant: ApplicantModel;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private cdr: ChangeDetectorRef,
        private sharedKeyDataService: SharedkeyDataService,
        private financeService: FinanceService) {

        this.sharedKeyDataService.keyIdentifier.subscribe(data => {
            this.keyIdentifier = data;
            this.application = this.keyIdentifier.application;
            this.otherOwners = this.application.applicants.filter(apl => apl.applicantType === ApplicantType.OTHER);
        });

        this.createOwnershipForm();

    }

    createOwnershipForm() {
        this.ownershipForm = this.formBuilder.group({
            owners: this.formBuilder.array([])
        });

        if (this.otherOwners.length > 0) {
            this.otherOwners.forEach(owner => {
                this.addOwner(owner);
            });
        } else {
            this.addOwner(new ApplicantModel());
        }
    }

    private isEntityOwner(owner: ApplicantModel): boolean {
        return !!owner.companyLegalName;
    }

    get businessApplicantName(): string | undefined {
        return this.application?.applicants?.find(apl => apl.applicantType === ApplicantType.BUSINESS)?.companyLegalName;
    }

    get owners(): FormArray {
        return this.ownershipForm.get('owners') as FormArray;
    }

    updateAdditionalOwner() {
        const ownerList = [];
        this.owners.controls.forEach(owner => {
            ownerList.push(owner.value);
        });
        this.financeService.otherOwners$.next(ownerList);
    }

    /**
     * Add owner Form into formGroup*
     * @param owner
     */
    addOwner(owner: ApplicantModel) {
        const isEntity = this.isEntityOwner(owner);
        // Wire shape confirmed via GET application: nested owners come back as
        // applicant.businessOwners, symmetric with what PUT accepts.
        const existingBeneficialOwners: Partial<BeneficialOwnerModel>[] = owner.businessOwners || [];

        const ownerGroup = this.formBuilder.group({
            applicantId: [owner.applicantId || undefined, null],

            hasQualifyingBeneficialOwner: [
                (owner as any).hasQualifyingBeneficialOwner || (existingBeneficialOwners.length > 0 ? 'yes' : null),
                isEntity ? Validators.required : null
            ],
            noQualifyingOwnerAfterDisclosure: [(owner as any).noQualifyingOwnerAfterDisclosure || false],
            originalBusinessOwnerIds: [existingBeneficialOwners.map(bo => bo.applicantId).filter(id => !!id)],
            beneficialOwners: this.formBuilder.array(
                isEntity ? existingBeneficialOwners.map(bo => this.buildBeneficialOwnerGroup(bo)) : []
            ),
            firstName: [owner.firstName || '', isEntity ? null : Validators.required],
            lastName: [owner.lastName || '', isEntity ? null : Validators.required],
            companyLegalName: [owner.companyLegalName || '', isEntity ? [Validators.required, Validators.pattern("^[a-zA-Z0-9-'. ]+$")] : null],
            title: [owner.title || ''],
            percentageOwnership: [owner.percentageOwnership || undefined, Validators.required],
            option: [isEntity ? 'entity' : 'individual', Validators.required]
        });

        this.owners.push(ownerGroup);
    }

    private buildBeneficialOwnerGroup(bo?: Partial<BeneficialOwnerModel>): FormGroup {
        return this.formBuilder.group({
            applicantId: [(bo && bo.applicantId) || undefined],
            firstName: [(bo && bo.firstName) || '', Validators.required],
            lastName: [(bo && bo.lastName) || '', Validators.required],
            title: [(bo && bo.title) || ''],
            percentageOwnership: [(bo && bo.percentageOwnership) || undefined, Validators.required]
        });
    }

    getRemovedBusinessOwnerIds(): number[] {
        const removedIds: number[] = [];
        this.owners.controls.forEach(ownerGroup => {
            const originalIds: number[] = ownerGroup.get('originalBusinessOwnerIds').value || [];
            if (!originalIds.length) {
                return;
            }
            const currentIds = (ownerGroup.get('beneficialOwners') as FormArray).controls
                .map(control => control.get('applicantId').value)
                .filter(id => !!id);
            originalIds.forEach(id => {
                if (!currentIds.includes(id)) {
                    removedIds.push(id);
                }
            });
        });
        return removedIds;
    }

    getBeneficialOwners(ownerIndex: number): FormArray {
        const ownerGroup = this.owners ? this.owners.at(ownerIndex) : null;
        if (!ownerGroup) {
            return null;
        }
        return ownerGroup.get('beneficialOwners') as FormArray;
    }

    onHasQualifyingBeneficialOwnerChange(ownerIndex: number): void {
        const ownerGroup = this.owners ? this.owners.at(ownerIndex) : null;
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (!ownerGroup || !beneficialOwners) {
            return;
        }
        if (ownerGroup.get('hasQualifyingBeneficialOwner').value === 'yes') {
            if (beneficialOwners.length === 0) {
                beneficialOwners.push(this.buildBeneficialOwnerGroup());
            }
        } else {
            while (beneficialOwners.length) {
                beneficialOwners.removeAt(0);
            }
        }
    }

    addBeneficialOwner(ownerIndex: number): void {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (beneficialOwners) {
            beneficialOwners.push(this.buildBeneficialOwnerGroup());
            this.cdr.detectChanges();
        }
    }

    removeBeneficialOwner(ownerIndex: number, boIndex: number) {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (beneficialOwners) {
            beneficialOwners.removeAt(boIndex);
            // Removing a row can bring the total back under 100% — clear any stale errors.
            this.onBeneficialOwnerPercentageChange(ownerIndex);
        }
    }

    /**
     * Checks live (on keystroke)
     */
    isEntityPercentageValid(ownerIndex: number): boolean {
        const ownerGroup = this.owners ? this.owners.at(ownerIndex) : null;
        const pct = ownerGroup ? Number(ownerGroup.get('percentageOwnership')!.value) : NaN;
        return pct >= 25 && pct <= 100;
    }

    /**
     * Dynamically scaled 25% CDD threshold: an entity holding X% of the applicant requires its
     * own owners at >= 25/X% (capped at 100%) to reach the 25% effective threshold. Rounded up,
     */
    getRequiredBeneficialOwnerPct(ownerIndex: number): number {
        const ownerGroup = this.owners ? this.owners.at(ownerIndex) : null;
        const entityPct = ownerGroup ? this.roundUpPercentage(ownerGroup.get('percentageOwnership')!.value) : 0;
        if (!entityPct) {
            return 100;
        }
        return Math.min(100, Math.ceil(2500 / entityPct));
    }

 
    private roundUpPercentage(value: any): number {
        return Math.ceil(Number(value) || 0);
    }

    getBeneficialOwnersTotalPercentage(ownerIndex: number): number {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (!beneficialOwners) {
            return 0;
        }
        return beneficialOwners.controls.reduce(
            (total, control) => total + this.roundUpPercentage(control.get('percentageOwnership')!.value), 0
        );
    }

    /**
     * True once the entity's ownership has been fully disclosed (its beneficial owners sum to
     * 100%) and NONE of those disclosed individuals individually clears the required threshold.
     * Gates the "confirm no qualifying owner" checkbox — see noQualifyingOwnerAfterDisclosure.
     */
    noneOfBeneficialOwnersQualify(ownerIndex: number): boolean {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (!beneficialOwners || beneficialOwners.length === 0) {
            return false;
        }
        const required = this.getRequiredBeneficialOwnerPct(ownerIndex);
        return beneficialOwners.controls.every(
            control => (Number(control.get('percentageOwnership').value) || 0) < required
        );
    }

    isAddBeneficialOwnerDisabled(ownerIndex: number): boolean {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (!beneficialOwners || beneficialOwners.length === 0) {
            return false;
        }

        const hasInvalidRow = beneficialOwners.controls.some(
            control => control.get('percentageOwnership').invalid
        );
        if (hasInvalidRow) {
            return true;
        }

        const required = this.getRequiredBeneficialOwnerPct(ownerIndex);
        const remaining = 100 - this.getBeneficialOwnersTotalPercentage(ownerIndex);
        return remaining < required;
    }

    onBeneficialOwnerPercentageChange(ownerIndex: number): void {
        const beneficialOwners = this.getBeneficialOwners(ownerIndex);
        if (!beneficialOwners) {
            return;
        }
        const total = this.getBeneficialOwnersTotalPercentage(ownerIndex);
        beneficialOwners.controls.forEach(control => {
            const percentageControl = control.get('percentageOwnership');
            if (total > 100) {
                percentageControl.setErrors({...percentageControl.errors, 'bo-total-percentage': true});
            } else if (percentageControl.hasError('bo-total-percentage')) {
                const errors = {...percentageControl.errors};
                delete errors['bo-total-percentage'];
                percentageControl.setErrors(Object.keys(errors).length ? errors : null);
            }
        });
    }

    resetFormArrayNode(e, index) {
        const group = this.owners.controls[index];

        if (group.value.option === 'entity') {
            group.get('companyLegalName').setValidators([Validators.required, Validators.pattern("^[a-zA-Z0-9-'. ]+$")]);
            // Clear the individual fields' stale values, not just their validators — otherwise a
            // name typed before switching to Entity would still be sent to the backend for a row
            // the UI no longer shows it on.
            group.get('firstName').setValue('');
            group.get('firstName').clearValidators();
            group.get('lastName').setValue('');
            group.get('lastName').clearValidators();
            group.get('title').setValue('');
            group.get('title').clearValidators();


            group.get('hasQualifyingBeneficialOwner').setValidators(Validators.required);
        } else {

            group.get('companyLegalName').setValue('');
            group.get('companyLegalName').clearValidators();
            group.get('firstName').setValidators(Validators.required);
            group.get('lastName').setValidators(Validators.required);
            group.get('title').setValidators(Validators.required);

          
            group.get('hasQualifyingBeneficialOwner').setErrors(null);
            group.get('hasQualifyingBeneficialOwner').clearValidators();
            group.get('hasQualifyingBeneficialOwner').setValue(null);
            group.get('noQualifyingOwnerAfterDisclosure').setValue(false);

            const beneficialOwners = group.get('beneficialOwners') as FormArray;
            if (beneficialOwners) {
                while (beneficialOwners.length) {
                    beneficialOwners.removeAt(0);
                }
            }
        }

        group.get('companyLegalName').updateValueAndValidity();
        group.get('firstName').updateValueAndValidity();
        group.get('lastName').updateValueAndValidity();
        group.get('title').updateValueAndValidity();
        group.get('hasQualifyingBeneficialOwner').updateValueAndValidity();
    }

    /**
     * Tooltip for an owner row's remove button — names the entity/individual being removed,
     * with the name in parentheses only when one has actually been entered.
     */
    getRemoveOwnerTooltip(ownerIndex: number): string {
        const ownerGroup = this.owners ? this.owners.at(ownerIndex) : null;
        if (!ownerGroup) {
            return '';
        }
        if (ownerGroup.get('option').value === 'entity') {
            const name = (ownerGroup.get('companyLegalName').value || '').trim();
            return 'Remove This Entity' + (name ? ` (${name})` : '');
        }
        const fullName = [ownerGroup.get('firstName').value, ownerGroup.get('lastName').value]
            .map(part => (part || '').trim())
            .filter(Boolean)
            .join(' ');
        return 'Remove This Individual' + (fullName ? ` (${fullName})` : '');
    }

    removeOwner(index: number) {
        this.owners.removeAt(index);
        this.updateAdditionalOwner();
    }

    /**
     * Function for check logic of total ownership percentage*
     */
    getTotalPercentage() {
        let total = 0;
        this.owners.controls.forEach((owner, index) => {
            total += this.roundUpPercentage(owner.get('percentageOwnership')!.value);

            if (total > 100) {
                this.owners.controls[index].get('percentageOwnership').setErrors({'total-percentage': true});
            }
            if (total < 25) {
                this.owners.controls[index].get('percentageOwnership').setErrors({'total-percentage': true});
            }
        });

        /*if no owner added*/
        if (total < 25) {
            this.owners.controls[0].get('percentageOwnership').setErrors({'total-percentage': true});
        }
        if (total < 75) {
            this.addOwner(new ApplicantModel());
        }
    }

    runValidation() {
        if (!this.certifiedOwnersBox) {
            this.showErrorForCertifiedBox = true;
        }
        this.ownershipForm.markAllAsTouched();
        this.companyOtherOwnersForm.form.markAllAsTouched();
    }

    registerOnChange(fn: any): void {
    }

    registerOnTouched(fn: any): void {
    }

    writeValue(obj: any): void {
        this.otherOwners = obj;
    }
}
